import { BreakpointObserver } from "@angular/cdk/layout";
import { ChangeDetectionStrategy, Component, Input, OnInit, input, inject } from "@angular/core";
import { Observable, timer } from "rxjs";
import { map, mapTo, startWith, switchMapTo } from "rxjs/operators";
import { OBSERVER_BREAKPOINT_MD_MIN } from "src/app/constants/technical.constants";
import { FmsKey } from "src/app/services/translations";
import { LetDirective } from "../../directives/let.directive";
import { ButtonModule } from "@storeblocks/button-ng";
import { NgTemplateOutlet, AsyncPipe } from "@angular/common";
import { RouterLink } from "@angular/router";
import { FmsPipe } from "../../pipes/fms.pipe";
import { LoadingIndicatorLinearModule } from "@storeblocks/loading-indicator-linear-ng";

export interface NavProgressButton {
  visible?: boolean;
  text?: FmsKey;
  link?: string;
  onClick?: () => void;
}

export interface NavProgressButtons {
  next?: NavProgressButton;
  previous?: NavProgressButton;
}

@Component({
  selector: "app-nav-progress",
  templateUrl: "./nav-progress.component.html",
  styleUrls: ["./nav-progress.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LetDirective, ButtonModule, NgTemplateOutlet, RouterLink, AsyncPipe, FmsPipe, LoadingIndicatorLinearModule],
})
export class NavProgressComponent implements OnInit {
  private readonly breakpointObserver = inject(BreakpointObserver);

  @Input()
  public readonly progress!: number | null;

  /**
   * When passing this prop to the component using the Async pipe
   * ([buttons]="stream$ | async"), some emissions may get lost due to what we
   * assume is a limitation of the Async pipe combined with a possible
   * race condition in the change detection / render logic of Angular.
   *
   * When multiple emissions are fired almost simultaneoulsy (only a few ms
   * separating them), only the first emission is actually passed to the input.
   *
   * Expecting an Observable and using the Async pipe directly in the
   * template have resolved this issue, making the change detection work
   * as exepcted. Change with caution.
   */
  public readonly buttons$ = input.required<Observable<NavProgressButtons>>();

  public isTablet$?: Observable<boolean>;
  public readyToNavigateDebouncer$?: Observable<boolean>;

  public ngOnInit(): void {
    this.isTablet$ = this.breakpointObserver
      .observe(OBSERVER_BREAKPOINT_MD_MIN)
      .pipe(map(({ matches: isDesktop }) => !isDesktop));

    this.readyToNavigateDebouncer$ = this.buttons$().pipe(switchMapTo(timer(0).pipe(mapTo(true), startWith(false))));
  }
}
